import {
    Form as ShadForm,
    FormItem as ShadFormItem,
    FormMessage as ShadFormMessage,
    useFormField
} from '@/components/ui/Form';
import { Label as ShadLabel } from '@/components/ui/Label';
import { Input } from '@/components/wm';
import { cn } from '@/utils';
import { ChevronLeft, HelpCircle } from 'lucide-react';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

const Form = React.forwardRef(({ className, onSubmit, id, ...props }, ref) => {
    const generatedId = React.useId();

    return (
        <div className={cn('tw-max-w-md tw-grid tw-gap-4', className)}>
            <ShadForm
                className={className}
                id={id ?? generatedId}
                onSubmit={onSubmit}
                ref={ref}
                {...props}
            />
        </div>
    );
});
Form.displayName = 'Form';

const FormInput = React.forwardRef(({ className, id, ...props }, ref) => {
    const generatedId = React.useId();

    return (
        <Input
            className={cn('', className)}
            id={id ?? generatedId}
            ref={ref}
            {...props}
        />
    );
});
FormInput.displayName = 'FormInput';

const FormLabel = React.forwardRef(({ className, id, htmlFor, ...props }, ref) => {
    const generatedId = React.useId();
    const labelId = id ?? generatedId;

    return (
        <ShadLabel
            className={cn('tw-inline-block tw-ml-2', className)}
            htmlFor={htmlFor ?? labelId}
            id={labelId}
            ref={ref}
            {...props}
        />
    );
});
FormLabel.displayName = 'FormLabel';

const FormMessage = React.forwardRef(({ className, children, ...props }, ref) => {
    return (
        <ShadFormMessage
            className={cn('tw-text-xs tw-font-light tw-absolute tw-right-2 tw-top-3 !tw-mt-0 tw-mb-0 tw-leading-none tw-italic', className)}
            ref={ref}
            {...props}
        />
    );
});
FormMessage.displayName = 'FormMessage';

const FormItem = React.forwardRef(({ className, children, itemInfo, ...props }, ref) => {
    return (
        <ShadFormItem
            className={cn('tw-space-y-1', className)}
            ref={ref}
            {...props}
        >
            {/* 1. This wrapper is necessary because the position relative was breaking shadcn form components */}
            <div className="tw-relative tw-h-0 tw-w-full tw-pointer-events-none tw-z-50">
                <SafeFormMessage/>

                {itemInfo && (
                    <FormItemInfo className="tw-pointer-events-auto">
                        {itemInfo}
                    </FormItemInfo>
                )}
            </div>
            <div>
                {children}
            </div>
        </ShadFormItem>
    );
});
FormItem.displayName = 'FormItem';

const FormItemInfo = React.forwardRef(({ className, children, ...props }, ref) => {
    const [isExpanded, setIsExpanded] = React.useState(true);

    if (!children) {
        return null;
    }

    return (
        <div
            className={cn(
                'tw-absolute tw-left-full tw-ml-4 tw-top-7 tw-z-50 tw-hidden lg:tw-block tw-pointer-events-auto',
                className
            )}
            ref={ref}
        >
            <div
                className={cn(
                    'tw-relative tw-bg-accent tw-border-l-2 tw-border-primary/20 tw-rounded-r-md tw-shadow-sm tw-cursor-pointer tw-transition-colors hover:tw-bg-accent/80',
                    isExpanded
                        ? 'tw-w-64 xl:tw-w-80 2xl:tw-w-[28rem]'
                        : 'tw-w-10'
                )}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="tw-flex tw-items-start tw-p-2">
                    {/* The Icon Trigger */}
                    <div className="tw-flex-none tw-w-6 tw-flex tw-justify-center tw-pt-1">
                        {isExpanded ? (
                            <ChevronLeft className="tw-h-4 tw-w-4 tw-text-primary"/>
                        ) : (
                            <HelpCircle className="tw-h-4 tw-w-4 tw-text-muted-foreground"/>
                        )}
                    </div>

                    {/* Content: Only rendered when expanded to prevent invisible layout shifts */}
                    {isExpanded && (
                        <div className="tw-pl-2 tw-pr-2 tw-pb-2">
                            <p className="tw-text-xs tw-text-muted-foreground tw-italic tw-leading-relaxed">
                                {children}
                            </p>
                        </div>
                    )}
                </div>

                {/* Decorative Arrow */}
                <div className="tw-absolute tw-left-[-5px] tw-top-3 tw-w-2 tw-h-2 tw-bg-accent tw-border-l tw-border-b tw-border-primary/20 tw-rotate-45"/>
            </div>
        </div>
    );
});
FormItemInfo.displayName = 'FormItemInfo';

// useFormField throws outside a FormProvider (useFormContext() returns null
// there). The old version wrapped the hook in try/catch, which violates the
// rules of hooks — a hook that throws mid-render can corrupt hook ordering.
// Instead, hoist the condition to a component boundary: the outer component
// checks for form context and only then mounts the inner one, whose hooks
// always run unconditionally.
const SafeFormMessageInner = ({ className }) => {
    const { error } = useFormField();

    if (!error) {
        return null;
    }

    return (
        <FormMessage className={cn('tw-pointer-events-auto', className)}/>
    );
};

const SafeFormMessage = ({ className }) => {
    const formContext = useFormContext();

    if (!formContext) {
        // No form in scope — render nothing, as before.
        return null;
    }

    return <SafeFormMessageInner className={className}/>;
};

export {
    Form,
    FormInput,
    FormItem,
    FormLabel,
    FormMessage,
    FormItemInfo
};
